import { DestroyRef, inject, Injectable, signal, WritableSignal } from '@angular/core';
import { BillingPeriod, BillingPeriodService } from '@eadsysteme/billingclient-v1';
import { Observable, throwError } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslocoService } from '@jsverse/transloco';
import { SessionService } from '../../../services/session.service';
import { STATUS } from 'src/app/services/data/STATUS';

@Injectable({
	providedIn: 'root',
})
export class PeriodService {
	private destroyRef = inject(DestroyRef);
	private translocoService = inject(TranslocoService);
	private sessionService = inject(SessionService);
	private billingPeriodService = inject(BillingPeriodService);

	selectedPeriod: WritableSignal<BillingPeriod> = signal(undefined);
	finishedGenerating = signal<boolean>(true);
	periodGeneratingStatus = signal<STATUS>(STATUS.UNDEFINED);
	copyCostStatus = signal<STATUS>(STATUS.UNDEFINED);
	copyStockStatus = signal<STATUS>(STATUS.UNDEFINED);

	async generatePeriod(billingId: string, predecessorPeriod: BillingPeriod, errorCallback?: (message: string) => void): Promise<boolean> {
		this.finishedGenerating.set(false);
		this.periodGeneratingStatus.set(STATUS.UNDEFINED);
		this.copyCostStatus.set(STATUS.UNDEFINED);
		this.copyStockStatus.set(STATUS.UNDEFINED);
		try {
			const newPeriod = await this.executeStep(
				() => {
					// TODO(billingclient-v637): createNextBillingPeriodBillingPeriod erfordert zwingend eine predecessor-ExportId.
					// Initial-Erzeugung (kein predecessor) ist im neuen API über createBillingPeriod + BillingPeriodRequest abzubilden — Backend-Klärung offen.
					if (!predecessorPeriod?.exportId) {
						return throwError(() => new Error('errors.billingGeneratePeriod'));
					}
					return this.billingPeriodService.createNextBillingPeriodBillingPeriod(
						this.sessionService.effectiveArea,
						billingId,
						predecessorPeriod.exportId
					);
				},
				this.periodGeneratingStatus,
				'errors.billingGeneratePeriod'
			);
			await this.executeStep(
				() => this.billingPeriodService.copyCostBillingPeriod(this.sessionService.effectiveArea, billingId, newPeriod.exportId),
				this.copyCostStatus,
				'errors.billingCopyCost'
			);

			await this.executeStep(
				() => this.billingPeriodService.copyStockBillingPeriod(this.sessionService.effectiveArea, billingId, newPeriod.exportId),
				this.copyStockStatus,
				'errors.billingCopyStock'
			);
			this.finishedGenerating.set(true);
			return true;
		} catch (error) {
			this.finishedGenerating.set(true);
			if (errorCallback && typeof errorCallback === 'function') {
				if (typeof error === 'string') {
					errorCallback(this.translocoService.translate(error));
				} else {
					errorCallback(this.translocoService.translate('errors.billingGeneratePeriod'));
				}
			}
			return false;
		}
	}

	private async executeStep<T>(operation: () => Observable<T>, statusSignal: WritableSignal<STATUS>, errorMessageKey: string): Promise<T> {
		return new Promise((resolve, reject) => {
			statusSignal.set(STATUS.PENDING);

			operation()
				.pipe(takeUntilDestroyed(this.destroyRef))
				.subscribe({
					next: async (result) => {
						await this.delay(500);
						statusSignal.set(STATUS.SUCCESS);
						resolve(result);
					},
					error: () => {
						statusSignal.set(STATUS.ERROR);
						reject(errorMessageKey);
					},
				});
		});
	}

	delay(ms: number) {
		return new Promise((resolve) => setTimeout(resolve, ms));
	}
}
